import React, { useState, useEffect, useRef } from 'react';
import BackButton from '@/components/BackButton';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import useEmployeesMap from '@/hooks/useEmployeesMap';
import useEmployeeBadges from '@/hooks/useEmployeeBadges';
import axios from 'axios';
import { fmtKsa, todayKsa } from '@/utils/formatKsa';
import { formatHoursHM } from '@/utils/formatHours';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar, Clock, UploadSimple, DownloadSimple, FileXls, CheckCircle, WarningCircle, ShieldSlash, ArrowCounterClockwise, ClipboardText, ShieldWarning, Plus, PencilSimple, Trash, Wrench, UserMinus, ClockClockwise, MagnifyingGlass, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { EmployeeName } from '@/components/EmployeeName';
import AttendanceCorrections from './AttendanceCorrections';
import SecurityAudit from './SecurityAudit';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

// Flag-label translations, mirrored from backend `utils/location_security.py`.
const FLAG_LABELS_AR = {
  vpn_suspected_org: 'اشتباه VPN/Proxy (الـIP من مزود VPN معروف)',
  ip_country_mismatch: 'الـIP من دولة مختلفة عن موقع العمل',
  ip_distance_extreme: 'اختلاف ضخم بين GPS والـIP (أكثر من 750 كم)',
  ip_distance_suspicious: 'اختلاف ملحوظ بين GPS والـIP (أكثر من 75 كم)',
  fake_gps_metadata: 'بيانات GPS غير طبيعية — اشتباه Fake-GPS',
  teleportation: 'انتقال غير منطقي بين بصمتين متتاليتين',
};
const FLAG_LABELS_EN = {
  vpn_suspected_org: 'VPN/Proxy suspected (IP belongs to known VPN/datacenter)',
  ip_country_mismatch: 'IP geolocation country differs from clinic',
  ip_distance_extreme: 'Extreme GPS-vs-IP distance (>750 km)',
  ip_distance_suspicious: 'Notable GPS-vs-IP distance (>75 km)',
  fake_gps_metadata: 'Suspicious GPS metadata — fake-GPS app',
  teleportation: 'Impossible movement between consecutive punches',
};

const SEVERITY_STYLE = {
  high: { color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
  medium: { color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  low: { color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200' },
};

function collectFlags(record) {
  const flags = [];
  let severity = 'ok';
  const inFlags = record?.clock_in_security_flags || [];
  const outFlags = record?.clock_out_security_flags || [];
  const inSev = record?.clock_in_security_severity || 'ok';
  const outSev = record?.clock_out_security_severity || 'ok';
  inFlags.forEach((f) => flags.push({ flag: f, phase: 'in' }));
  outFlags.forEach((f) => flags.push({ flag: f, phase: 'out' }));
  const rank = { ok: 0, low: 1, medium: 2, high: 3 };
  if (rank[outSev] > rank[severity]) severity = outSev;
  if (rank[inSev] > rank[severity]) severity = inSev;
  return { flags, severity };
}

function SecurityBadge({ record, language }) {
  const { flags, severity } = collectFlags(record);
  if (!flags.length) return null;
  const labels = language === 'ar' ? FLAG_LABELS_AR : FLAG_LABELS_EN;
  const style = SEVERITY_STYLE[severity] || SEVERITY_STYLE.low;
  const tip = flags
    .map(({ flag, phase }) => `• [${phase === 'in' ? (language === 'ar' ? 'دخول' : 'in') : (language === 'ar' ? 'خروج' : 'out')}] ${labels[flag] || flag}`)
    .join('\n');
  return (
    <span
      className={`inline-flex items-center justify-center w-5 h-5 rounded-full border ${style.bg} ${style.color} cursor-help`}
      title={tip}
      data-testid={`security-badge-${record.user_id}-${record.date}`}
    >
      <ShieldWarning size={12} weight="fill" />
    </span>
  );
}


// ── Admin Audit Log (edits / creates / deletes) ─────────────────────
const ACTION_LABELS = {
  delete: { ar: 'حذف', en: 'Delete', cls: 'bg-red-100 text-red-700 border-red-200' },
  edit:   { ar: 'تعديل', en: 'Edit', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  create: { ar: 'إنشاء يدوي', en: 'Manual Create', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  restore:{ ar: 'تراجع', en: 'Restore', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
};

// Format a datetime value for the audit table. The backend stores all
// timestamps in UTC ISO format; convert to Asia/Riyadh (UTC+3) for the
// admin's display so the times match the wall clock in the clinic.
// Date-only strings (YYYY-MM-DD) and non-datetime values are returned
// unchanged.
function _toRiyadh(isoStr) {
  if (!isoStr) return '—';
  // We accept both "YYYY-MM-DDTHH:MM:SS..." and "YYYY-MM-DD HH:MM..." shapes.
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(String(isoStr));
  if (!m) return String(isoStr);
  // Add the Z so the Date constructor parses as UTC, then shift +3h.
  const d = new Date(`${m[1]}T${m[2]}:00Z`);
  if (isNaN(d.getTime())) return String(isoStr);
  const ksa = new Date(d.getTime() + 3 * 60 * 60 * 1000);
  const Y = ksa.getUTCFullYear();
  const M = String(ksa.getUTCMonth() + 1).padStart(2, '0');
  const D = String(ksa.getUTCDate()).padStart(2, '0');
  const h = String(ksa.getUTCHours()).padStart(2, '0');
  const mm = String(ksa.getUTCMinutes()).padStart(2, '0');
  return `${Y}-${M}-${D} ${h}:${mm}`;
}

function fmtCell(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    return _toRiyadh(v);
  }
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function DiffViewer({ before, after, isAr }) {
  if (!before && !after) return <span className="text-[#655dc6]/60">—</span>;
  const trackedKeys = ['clock_in', 'clock_out', 'work_hours', 'overtime_hours', 'late_minutes', 'late_penalty', 'status', 'date', 'user_id', 'user_name'];
  const keys = new Set();
  trackedKeys.forEach(k => {
    if ((before && k in before) || (after && k in after)) keys.add(k);
  });
  // Add any extra changed keys not in trackedKeys
  if (before && after) {
    Object.keys({ ...before, ...after }).forEach(k => {
      if (k === 'id' || k === '_id') return;
      if (fmtCell(before[k]) !== fmtCell(after[k])) keys.add(k);
    });
  }
  return (
    <div className="text-xs space-y-0.5">
      {[...keys].map(k => {
        const b = before ? before[k] : undefined;
        const a = after ? after[k] : undefined;
        const changed = fmtCell(b) !== fmtCell(a);
        if (!before) {
          return (
            <div key={k} className="flex gap-2">
              <span className="text-[#655dc6]/60 min-w-[110px]">{k}:</span>
              <span className="text-emerald-700 font-mono">{fmtCell(a)}</span>
            </div>
          );
        }
        if (!after) {
          return (
            <div key={k} className="flex gap-2">
              <span className="text-[#655dc6]/60 min-w-[110px]">{k}:</span>
              <span className="text-red-700 line-through font-mono">{fmtCell(b)}</span>
            </div>
          );
        }
        return (
          <div key={k} className="flex gap-2">
            <span className="text-[#655dc6]/60 min-w-[110px]">{k}:</span>
            {changed ? (
              <>
                <span className="text-red-700 line-through font-mono">{fmtCell(b)}</span>
                <span className="text-[#655dc6]/40">→</span>
                <span className="text-emerald-700 font-mono">{fmtCell(a)}</span>
              </>
            ) : (
              <span className="font-mono text-[#655dc6]/70">{fmtCell(a)}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AdminAuditLogView({ language }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [restoring, setRestoring] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);
  const isAr = language === 'ar';

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/attendance/audit-log?limit=300`, { withCredentials: true });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const doRestore = async (row) => {
    setRestoring(row.id);
    try {
      await axios.post(`${API}/admin/attendance/audit-log/${row.id}/restore`, {}, { withCredentials: true });
      toast.success(isAr ? 'تم التراجع بنجاح' : 'Restore complete');
      setConfirmRow(null);
      await reload();
    } catch (err) {
      const detail = err?.response?.data?.detail || (isAr ? 'فشل التراجع' : 'Restore failed');
      toast.error(detail);
    } finally {
      setRestoring(null);
    }
  };

  const filtered = filterAction === 'all' ? rows : rows.filter(r => r.action === filterAction);

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-xl p-4 border border-[#655dc6]/10 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[#655dc6] flex items-center gap-2">
            <ClockClockwise size={18} weight="duotone" />
            {isAr ? 'سجل تدقيق التعديلات الإدارية' : 'Admin Edit / Delete / Create Audit Log'}
          </h3>
          <div className="flex gap-1">
            {['all', 'delete', 'edit', 'create', 'restore'].map(a => (
              <button
                key={a}
                onClick={() => setFilterAction(a)}
                className={`text-xs px-2.5 py-1 rounded-md border transition ${filterAction === a ? 'bg-[#655dc6] text-white border-[#655dc6]' : 'bg-white text-[#655dc6] border-[#655dc6]/30 hover:bg-[#655dc6]/5'}`}
                data-testid={`audit-filter-${a}`}
              >
                {a === 'all' ? (isAr ? 'الكل' : 'All')
                  : a === 'delete' ? (isAr ? 'حذف' : 'Delete')
                  : a === 'edit' ? (isAr ? 'تعديل' : 'Edit')
                  : a === 'create' ? (isAr ? 'إنشاء' : 'Create')
                  : (isAr ? 'تراجع' : 'Restore')}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs text-[#655dc6]/70 mb-3">
          {isAr
            ? 'سجل دائم لكل تعديل/حذف/إنشاء يدوي يقوم به الأدمن على بصمات الحضور. يمكن التراجع عن أي إجراء بزر "تراجع" — كل تراجع يُسجَّل بدوره كإجراء.'
            : 'Permanent log of every admin edit / delete / manual create on attendance punches. Each entry can be undone via the "Undo" button — every undo is itself recorded as an audit entry.'}
        </p>
        <div className="overflow-x-auto rounded-lg border border-[#C4BFF0]/30">
          <Table>
            <TableHeader>
              <TableRow className="border-[#C4BFF0]">
                <TableHead className="text-start whitespace-nowrap">{isAr ? 'الوقت' : 'When'}</TableHead>
                <TableHead className="text-start">{isAr ? 'الإجراء' : 'Action'}</TableHead>
                <TableHead className="text-start">{isAr ? 'الأدمن' : 'Admin'}</TableHead>
                <TableHead className="text-start">{isAr ? 'الموظف · التاريخ' : 'Employee · Date'}</TableHead>
                <TableHead className="text-start">{isAr ? 'السبب' : 'Reason'}</TableHead>
                <TableHead className="text-start">{isAr ? 'التفاصيل' : 'Details'}</TableHead>
                <TableHead className="text-center whitespace-nowrap">{isAr ? 'تراجع' : 'Undo'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-[#655dc6]/70">…</TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-[#655dc6]/70">
                    {isAr ? 'لا توجد سجلات' : 'No log entries'}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map(r => {
                  const meta = ACTION_LABELS[r.action] || { ar: r.action, en: r.action, cls: 'bg-gray-100 text-gray-700' };
                  const ref = r.before || r.after || {};
                  const canUndo = (r.action === 'delete' || r.action === 'edit' || r.action === 'create') && !r.restored_at;
                  return (
                    <TableRow key={r.id} className="border-[#C4BFF0]/50" data-testid={`audit-row-${r.id}`}>
                      <TableCell className="font-mono text-xs whitespace-nowrap text-[#655dc6]/80">
                        {_toRiyadh(r.at)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${meta.cls} text-xs`} data-testid={`audit-action-${r.action}`}>
                          {isAr ? meta.ar : meta.en}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{r.admin_name || '—'}</TableCell>
                      <TableCell className="text-sm">
                        <div className="font-medium text-[#655dc6]">{ref.user_name || '—'}</div>
                        <div className="text-xs text-[#655dc6]/70 font-mono">{ref.date || '—'}</div>
                      </TableCell>
                      <TableCell className="text-sm max-w-[200px]">
                        <span className="text-[#655dc6]/80">{r.reason || '—'}</span>
                      </TableCell>
                      <TableCell className="max-w-[420px]">
                        <DiffViewer before={r.before} after={r.after} isAr={isAr} />
                      </TableCell>
                      <TableCell className="text-center whitespace-nowrap">
                        {r.restored_at ? (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200"
                            title={isAr
                              ? `تم التراجع بواسطة ${r.restored_by_name || ''} في ${_toRiyadh(r.restored_at)}`
                              : `Restored by ${r.restored_by_name || ''} at ${_toRiyadh(r.restored_at)}`}
                          >
                            {isAr ? 'تم التراجع' : 'Restored'}
                          </span>
                        ) : canUndo ? (
                          <button
                            onClick={() => setConfirmRow(r)}
                            disabled={restoring === r.id}
                            className="text-xs px-2 py-1 rounded border bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100 disabled:opacity-50 flex items-center gap-1 mx-auto"
                            data-testid={`audit-undo-${r.id}`}
                          >
                            <ArrowCounterClockwise size={12} weight="bold" />
                            {isAr ? 'تراجع' : 'Undo'}
                          </button>
                        ) : (
                          <span className="text-[#655dc6]/30 text-xs">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Confirm undo */}
      <Dialog open={!!confirmRow} onOpenChange={(o) => !o && setConfirmRow(null)}>
        <DialogContent className="sm:max-w-md" dir={isAr ? 'rtl' : 'ltr'}>
          <DialogHeader>
            <DialogTitle className="text-amber-700 flex items-center gap-2">
              <ArrowCounterClockwise size={20} weight="duotone" />
              {isAr ? 'تأكيد التراجع' : 'Confirm undo'}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#655dc6]/80 space-y-2">
            <p>
              {isAr
                ? 'سيتم إعادة السجل إلى حالته السابقة. هذا الإجراء يُسجَّل بدوره في سجل التدقيق ولا يمكن إخفاؤه.'
                : 'The record will be reverted to its previous state. This action is itself logged in the audit trail and cannot be hidden.'}
            </p>
            {confirmRow && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs">
                <div><strong>{isAr ? 'الإجراء:' : 'Action:'}</strong> {ACTION_LABELS[confirmRow.action]?.[isAr ? 'ar' : 'en'] || confirmRow.action}</div>
                <div><strong>{isAr ? 'الموظف:' : 'Employee:'}</strong> {(confirmRow.before || confirmRow.after)?.user_name || '—'}</div>
                <div><strong>{isAr ? 'التاريخ:' : 'Date:'}</strong> {(confirmRow.before || confirmRow.after)?.date || '—'}</div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmRow(null)} data-testid="audit-undo-cancel">
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={() => doRestore(confirmRow)}
              disabled={!!restoring}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="audit-undo-confirm"
            >
              {restoring ? '…' : (isAr ? 'تأكيد التراجع' : 'Confirm undo')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminAttendance() {
  const { t } = useTranslation();
  const { language, direction } = useLanguage();
  const { user } = useAuth();
  const { empNo } = useEmployeesMap();
  const { getBadge } = useEmployeeBadges();
  // GM can OPEN this page (per policy: read-only oversight) but must NOT
  // see the destructive / admin-only action buttons. Only true admins can
  // add / edit / delete / recompute attendance rows.
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(todayKsa());
  // Free-text search filter — matches Arabic name, English name, and
  // employee number. Applied client-side over the already-loaded records.
  const [searchQuery, setSearchQuery] = useState('');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [waiveDialog, setWaiveDialog] = useState({ open: false, record: null, reason: '' });
  const [restoreDialog, setRestoreDialog] = useState({ open: false, record: null });
  const [waiving, setWaiving] = useState(false);
  // Bulk penalty waiver — set of `user_id` currently ticked in the
  // attendance table (only rows with an active late_penalty may be
  // selected). Persisted in state so the checkbox column stays in sync
  // when the table re-renders after a fetch.
  const [selectedForWaive, setSelectedForWaive] = useState(new Set());
  const [bulkWaiveDialog, setBulkWaiveDialog] = useState({ open: false, reason: '' });
  const [bulkWaiving, setBulkWaiving] = useState(false);
  // Admin direct attendance correction state
  const [addDialog, setAddDialog] = useState({ open: false, user_id: '', clock_in: '', clock_out: '', status: 'present', reason: '' });
  const [editDialog, setEditDialog] = useState({ open: false, record: null, clock_in: '', clock_out: '', status: 'present', reason: '', session_index: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, record: null, reason: '' });
  const [savingAdminAction, setSavingAdminAction] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [employees, setEmployees] = useState([]);
  // Absentees tab state
  const [absStart, setAbsStart] = useState(todayKsa());
  const [absEnd, setAbsEnd] = useState(todayKsa());
  const [absentees, setAbsentees] = useState([]);
  const [absLoading, setAbsLoading] = useState(false);
  const fileInputRef = useRef(null);
  // Track the id of the most recent fetch so out-of-order responses can be
  // ignored. This is the root fix for a subtle bug reported by the user:
  // when typing a date into the date input character-by-character, each
  // intermediate valid ISO string (e.g. `2026-07-01`, `2026-07-14`) fires
  // a fetch; the earlier (wrong-day) response could resolve AFTER the
  // later one, overwriting the correct data — so the table showed
  // fingerprints of a DIFFERENT day for the date visible in the header.
  const latestFetchIdRef = useRef(0);

  // Only trigger a fetch when the date input holds a complete, valid,
  // non-future ISO date. Manual typing produces incomplete strings that
  // we must ignore, otherwise we hammer the API with partial dates.
  const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
  const isValidKsaPastOrTodayDate = (d) => {
    if (!d || !ISO_DATE_RE.test(d)) return false;
    // Reject impossible days (e.g. 2026-02-31) by round-tripping through Date.
    const [y, m, day] = d.split('-').map(Number);
    if (y < 2020 || y > 2100) return false;
    const dt = new Date(Date.UTC(y, m - 1, day));
    if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== day) return false;
    // Never accept a date in the future (KSA).
    return d <= todayKsa();
  };

  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    if (!isValidKsaPastOrTodayDate(selectedDate)) {
      // Skip — user is mid-typing. Do not clobber the current table.
      return undefined;
    }
    const myId = ++latestFetchIdRef.current;
    // First mount fires IMMEDIATELY — the 350 ms debounce is only there
    // to coalesce keystrokes when the admin types into the date input.
    // Applying it on the initial render added a visible half-second
    // spinner to every page open which the users noticed.
    if (!initialLoadDoneRef.current) {
      initialLoadDoneRef.current = true;
      fetchAttendance(selectedDate, myId);
      return undefined;
    }
    const timer = setTimeout(() => {
      fetchAttendance(selectedDate, myId);
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    // Load employees once for the manual-record dropdown.
    (async () => {
      try {
        const { data } = await axios.get(`${API}/employees`, { withCredentials: true });
        setEmployees(Array.isArray(data) ? data : []);
      } catch (e) {
        // Silent — manual record dialog will simply show "no employees".
      }
    })();
  }, []);

  // ---- Saudi local <-> UTC ISO helpers for the admin correction dialogs.
  // Backend stores clock_in/clock_out as UTC ISO; UI shows Saudi local.
  const SAUDI_OFFSET_HOURS = 3;
  const toUtcIsoFromLocal = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;
    const [hh, mm] = timeStr.split(':').map(Number);
    const [yy, mo, dd] = dateStr.split('-').map(Number);
    const utc = new Date(Date.UTC(yy, mo - 1, dd, hh - SAUDI_OFFSET_HOURS, mm, 0));
    return utc.toISOString();
  };
  const fromUtcIsoToHHMM = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const saudi = new Date(d.getTime() + SAUDI_OFFSET_HOURS * 3600 * 1000);
    const hh = String(saudi.getUTCHours()).padStart(2, '0');
    const mn = String(saudi.getUTCMinutes()).padStart(2, '0');
    return `${hh}:${mn}`;
  };

  const fetchAttendance = async (dateArg, fetchId) => {
    const dateToUse = dateArg || selectedDate;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/attendance/all?date=${dateToUse}`, { withCredentials: true });
      // Ignore stale responses (see `latestFetchIdRef` comment) so the
      // table always reflects the LAST typed/picked date, never an
      // earlier one whose response happened to arrive last.
      if (fetchId != null && fetchId !== latestFetchIdRef.current) return;
      setAttendance(data);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    } finally {
      if (fetchId == null || fetchId === latestFetchIdRef.current) {
        setLoading(false);
      }
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    // Pin to Asia/Riyadh so a manager viewing from any timezone sees the
    // canonical Saudi clock — matches what the SAUDI_TZ_OFFSET-based
    // business rules on the backend operate against.
    // Use 24-hour format (HH:mm) per business standard.
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Asia/Riyadh',
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      present: 'bg-green-100 text-green-700 border-green-200',
      absent: 'bg-red-100 text-red-700 border-red-200',
      late: 'bg-yellow-100 text-yellow-700 border-yellow-200'
    };
    return variants[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const downloadTemplate = () => {
    window.open(`${API}/attendance/template`, '_blank');
  };

  const openWaiveDialog = (record) => {
    setWaiveDialog({ open: true, record, reason: '' });
  };

  const submitWaive = async () => {
    const r = waiveDialog.record;
    if (!r) return;
    setWaiving(true);
    try {
      await axios.post(
        `${API}/attendance/${r.user_id}/${r.date}/waive-penalty`,
        { reason: waiveDialog.reason },
        { withCredentials: true }
      );
      toast.success(language === 'ar' ? 'تم إعفاء الغرامة بنجاح' : 'Penalty waived successfully');
      setWaiveDialog({ open: false, record: null, reason: '' });
      fetchAttendance(selectedDate, ++latestFetchIdRef.current);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'تعذّر إعفاء الغرامة' : 'Failed to waive penalty'));
    } finally {
      setWaiving(false);
    }
  };

  // Bulk waive — apply the SAME reason to every ticked employee in one
  // round-trip. Rows without a penalty (or already waived) are skipped
  // by the backend; the response reports counts so we can toast a
  // meaningful summary.
  const submitBulkWaive = async () => {
    const userIds = Array.from(selectedForWaive);
    if (userIds.length === 0) return;
    setBulkWaiving(true);
    try {
      const { data } = await axios.post(
        `${API}/attendance/bulk-waive-penalty`,
        { date: selectedDate, user_ids: userIds, reason: bulkWaiveDialog.reason },
        { withCredentials: true }
      );
      const waived = data?.waived_count ?? 0;
      const skipped = (data?.skipped_already_waived ?? 0) + (data?.skipped_no_penalty ?? 0);
      toast.success(language === 'ar'
        ? `تم إعفاء ${waived} موظفاً${skipped ? ` — تم تخطي ${skipped}` : ''}`
        : `Waived ${waived} employees${skipped ? ` — skipped ${skipped}` : ''}`);
      setBulkWaiveDialog({ open: false, reason: '' });
      setSelectedForWaive(new Set());
      fetchAttendance(selectedDate, ++latestFetchIdRef.current);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'تعذّر الإعفاء الجماعي' : 'Bulk waive failed'));
    } finally {
      setBulkWaiving(false);
    }
  };

  const toggleRowSelected = (userId) => {
    setSelectedForWaive(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  // Clear selection whenever the visible day changes — the ticked
  // user_ids belong to the previous day's rows.
  useEffect(() => {
    setSelectedForWaive(new Set());
  }, [selectedDate]);

  const restorePenalty = async (record) => {
    try {
      await axios.post(
        `${API}/attendance/${record.user_id}/${record.date}/restore-penalty`,
        {},
        { withCredentials: true }
      );
      toast.success(language === 'ar' ? 'تم استرجاع الغرامة' : 'Penalty restored');
      setRestoreDialog({ open: false, record: null });
      fetchAttendance(selectedDate, ++latestFetchIdRef.current);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'تعذّر الاسترجاع' : 'Failed to restore'));
    }
  };

  // ---------- Admin direct attendance correction handlers ----------
  const openAddDialog = () => {
    setAddDialog({ open: true, user_id: '', clock_in: '', clock_out: '', status: 'present', reason: '' });
  };

  const recomputeDay = async () => {
    if (!window.confirm(language === 'ar'
      ? `إعادة حساب جميع سجلات يوم ${selectedDate} بناءً على الجداول الحالية؟ الإعفاءات المسجلة محفوظة.`
      : `Recompute every attendance record for ${selectedDate} using the current schedules? Existing waivers are preserved.`)) return;
    setRecomputing(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/attendance/recompute`,
        { start_date: selectedDate, end_date: selectedDate },
        { withCredentials: true }
      );
      toast.success(data.message || (language === 'ar' ? 'تم' : 'Done'));
      fetchAttendance(selectedDate, ++latestFetchIdRef.current);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'تعذّر إعادة الحساب' : 'Failed to recompute'));
    } finally {
      setRecomputing(false);
    }
  };

  // Export the currently-loaded attendance rows to a UTF-8-BOM CSV that
  // Excel opens correctly (with Arabic intact). Columns mirror what the
  // payroll team needs for verification: employee_number, name, clock-in,
  // clock-out, work_hours, overtime_hours, penalty.
  const exportAttendance = () => {
    if (!attendance || attendance.length === 0) {
      toast.error(language === 'ar' ? 'لا توجد سجلات لتصديرها' : 'No records to export');
      return;
    }
    const headers = language === 'ar'
      ? ['الرقم الوظيفي', 'اسم الموظف', 'الإسم بالإنجليزية', 'التاريخ', 'الحضور', 'الإنصراف', 'ساعات العمل', 'ساعات الإضافي', 'الجزاء (ريال)']
      : ['Employee #', 'Employee Name', 'Name (EN)', 'Date', 'Clock In', 'Clock Out', 'Work Hours', 'Overtime Hours', 'Penalty (SAR)'];

    // CSV cell escaper — quote any cell with comma/quote/newline.
    const escape = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const lines = [headers.map(escape).join(',')];
    for (const r of attendance) {
      const empNumber = empNo(r.user_id) || r.employee_number || '';
      const wh = r.work_hours_raw != null ? r.work_hours_raw : (r.work_hours != null ? r.work_hours : '');
      lines.push([
        empNumber,
        r.user_name || '',
        r.user_name_en || '',
        r.date || '',
        formatTime(r.clock_in) || '',
        formatTime(r.clock_out) || '',
        wh === '' ? '' : formatHoursHM(wh),
        (r.overtime_hours == null || r.overtime_hours === '') ? '0:00' : formatHoursHM(r.overtime_hours),
        (r.late_penalty == null || r.late_penalty === '') ? '0.00' : Number(r.late_penalty).toFixed(2),
      ].map(escape).join(','));
    }
    // \ufeff prefix is the UTF-8 BOM — Excel needs it to render Arabic right.
    const csv = '\ufeff' + lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_${selectedDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(language === 'ar' ? `تم تصدير ${attendance.length} سجلاً` : `Exported ${attendance.length} rows`);
  };

  const submitAddRecord = async () => {
    if (!addDialog.user_id) {
      toast.error(language === 'ar' ? 'اختر الموظف' : 'Select an employee');
      return;
    }
    if (!addDialog.reason.trim()) {
      toast.error(language === 'ar' ? 'سبب الإضافة مطلوب' : 'Reason is required');
      return;
    }
    setSavingAdminAction(true);
    try {
      await axios.post(
        `${API}/admin/attendance`,
        {
          user_id: addDialog.user_id,
          date: selectedDate,
          clock_in: toUtcIsoFromLocal(selectedDate, addDialog.clock_in),
          clock_out: toUtcIsoFromLocal(selectedDate, addDialog.clock_out),
          status: addDialog.status,
          reason: addDialog.reason.trim(),
        },
        { withCredentials: true }
      );
      toast.success(language === 'ar' ? 'تم إنشاء السجل' : 'Record created');
      setAddDialog({ open: false, user_id: '', clock_in: '', clock_out: '', status: 'present', reason: '' });
      fetchAttendance(selectedDate, ++latestFetchIdRef.current);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'تعذّر إنشاء السجل' : 'Failed to create record'));
    } finally {
      setSavingAdminAction(false);
    }
  };

  const openEditDialog = (record, sessionIndex = null) => {
    // If a specific session is being edited, pre-fill HH:MM from that
    // session's times so the admin sees the right starting values.
    let ciStr = record.clock_in;
    let coStr = record.clock_out;
    if (sessionIndex !== null && Array.isArray(record.sessions) && record.sessions[sessionIndex]) {
      ciStr = record.sessions[sessionIndex].clock_in;
      coStr = record.sessions[sessionIndex].clock_out;
    }
    setEditDialog({
      open: true,
      record,
      clock_in: fromUtcIsoToHHMM(ciStr),
      clock_out: fromUtcIsoToHHMM(coStr),
      status: record.status || 'present',
      reason: '',
      session_index: sessionIndex,
    });
  };

  const submitEditRecord = async () => {
    const r = editDialog.record;
    if (!r) return;
    if (!editDialog.reason.trim()) {
      toast.error(language === 'ar' ? 'سبب التعديل مطلوب' : 'Reason is required');
      return;
    }
    setSavingAdminAction(true);
    try {
      await axios.put(
        `${API}/admin/attendance/${r.id}`,
        {
          clock_in: editDialog.clock_in ? toUtcIsoFromLocal(r.date, editDialog.clock_in) : '',
          clock_out: editDialog.clock_out ? toUtcIsoFromLocal(r.date, editDialog.clock_out) : '',
          status: editDialog.status,
          reason: editDialog.reason.trim(),
          ...(editDialog.session_index !== null && editDialog.session_index !== undefined
            ? { session_index: editDialog.session_index } : {}),
        },
        { withCredentials: true }
      );
      toast.success(language === 'ar' ? 'تم تحديث السجل' : 'Record updated');
      setEditDialog({ open: false, record: null, clock_in: '', clock_out: '', status: 'present', reason: '', session_index: null });
      fetchAttendance(selectedDate, ++latestFetchIdRef.current);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'تعذّر تحديث السجل' : 'Failed to update record'));
    } finally {
      setSavingAdminAction(false);
    }
  };

  // ----- Absentees tab -----
  const fetchAbsentees = async () => {
    setAbsLoading(true);
    try {
      const { data } = await axios.get(`${API}/attendance/absentees`, {
        withCredentials: true,
        params: { start_date: absStart, end_date: absEnd },
      });
      setAbsentees(data.absentees || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'تعذّر جلب الغائبين' : 'Failed to fetch absentees'));
    } finally {
      setAbsLoading(false);
    }
  };

  // Map leave_type → Arabic/English label with a color hint.
  const leaveLabel = (lt) => {
    const map = {
      annual:      { ar: 'إجازة سنوية', en: 'Annual', cls: 'bg-emerald-100 text-emerald-700' },
      sick:        { ar: 'إجازة مرضية', en: 'Sick', cls: 'bg-rose-100 text-rose-700' },
      unpaid:      { ar: 'إجازة بدون راتب', en: 'Unpaid', cls: 'bg-slate-200 text-slate-700' },
      maternity:   { ar: 'إجازة أمومة', en: 'Maternity', cls: 'bg-pink-100 text-pink-700' },
      paternity:   { ar: 'إجازة أبوة', en: 'Paternity', cls: 'bg-blue-100 text-blue-700' },
      hajj:        { ar: 'إجازة حج', en: 'Hajj', cls: 'bg-amber-100 text-amber-800' },
      emergency:   { ar: 'إجازة طارئة', en: 'Emergency', cls: 'bg-orange-100 text-orange-700' },
      bereavement: { ar: 'إجازة عزاء', en: 'Bereavement', cls: 'bg-purple-100 text-purple-700' },
      marriage:    { ar: 'إجازة زواج', en: 'Marriage', cls: 'bg-fuchsia-100 text-fuchsia-700' },
    };
    return map[lt] || { ar: lt || '—', en: lt || '—', cls: 'bg-slate-100 text-slate-600' };
  };


  const submitDeleteRecord = async () => {
    const r = deleteDialog.record;
    if (!r) return;
    if (!deleteDialog.reason.trim()) {
      toast.error(language === 'ar' ? 'سبب الحذف مطلوب' : 'Reason is required');
      return;
    }
    setSavingAdminAction(true);
    try {
      await axios.delete(
        `${API}/admin/attendance/${r.id}`,
        { withCredentials: true, params: { reason: deleteDialog.reason.trim() } }
      );
      toast.success(language === 'ar' ? 'تم حذف السجل' : 'Record deleted');
      setDeleteDialog({ open: false, record: null, reason: '' });
      fetchAttendance(selectedDate, ++latestFetchIdRef.current);
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'تعذّر حذف السجل' : 'Failed to delete record'));
    } finally {
      setSavingAdminAction(false);
    }
  };


  const handleImport = async () => {
    if (!selectedFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const { data } = await axios.post(`${API}/attendance/import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(data);
      if (data.imported > 0) {
        toast.success(data.message);
        if (data.dates?.length > 0) {
          setSelectedDate(data.dates[0]);
        } else {
          fetchAttendance(selectedDate, ++latestFetchIdRef.current);
        }
      }
    } catch (error) {
      const msg = error.response?.data?.detail || t('import_failed');
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const resetImportDialog = () => {
    setShowImportDialog(false);
    setSelectedFile(null);
    setImportResult(null);
  };

  return (
    <div className="min-h-screen bg-[#F2F0FA]" data-testid="admin-attendance-page" dir={direction}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <h1 className="text-3xl font-semibold text-[#655dc6] flex items-center gap-2">
          <Clock size={28} weight="duotone" />
          {t('attendance')}
        </h1>
        <Tabs defaultValue="punches" className="w-full" data-testid="attendance-tabs" dir={direction}>
          <TabsList className="bg-white border border-[#655dc6]/10 w-full sm:w-auto overflow-x-auto no-scrollbar justify-start">
            <TabsTrigger value="punches" data-testid="tab-punches" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white gap-2 shrink-0">
              <Clock size={16} weight="duotone" /> {language === 'ar' ? 'الحضور والانصراف' : 'Attendance'}
            </TabsTrigger>
            <TabsTrigger value="corrections" data-testid="tab-corrections" className={`data-[state=active]:bg-[#655dc6] data-[state=active]:text-white gap-2 shrink-0 ${isAdmin ? '' : 'hidden'}`}>
              <ClipboardText size={16} weight="duotone" /> {language === 'ar' ? 'تصحيحات البصمة' : 'Corrections'}
            </TabsTrigger>
            <TabsTrigger value="absentees" data-testid="tab-absentees" className="data-[state=active]:bg-[#655dc6] data-[state=active]:text-white gap-2 shrink-0">
              <UserMinus size={16} weight="duotone" /> {language === 'ar' ? 'الغائبون' : 'Absentees'}
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security" className={`data-[state=active]:bg-[#655dc6] data-[state=active]:text-white gap-2 shrink-0 ${isAdmin ? '' : 'hidden'}`}>
              <ShieldWarning size={16} weight="duotone" /> {language === 'ar' ? 'التدقيق الأمني' : 'Security Audit'}
            </TabsTrigger>
            <TabsTrigger value="adminlog" data-testid="tab-adminlog" className={`data-[state=active]:bg-[#655dc6] data-[state=active]:text-white gap-2 shrink-0 ${isAdmin ? '' : 'hidden'}`}>
              <ClockClockwise size={16} weight="duotone" /> {language === 'ar' ? 'سجل تعديلات الأدمن' : 'Admin Audit Log'}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="punches" className="mt-4 space-y-6">
        <div className="flex justify-end">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto justify-start sm:justify-end">
            {isAdmin && (
              <>
                <Button
                  onClick={recomputeDay}
                  disabled={recomputing}
                  variant="outline"
                  className="border-orange-300 text-orange-700 hover:bg-orange-50 gap-2"
                  data-testid="admin-recompute-day-btn"
                  title={language === 'ar' ? 'يعيد حساب التأخر/الجزاء/الحالة لجميع سجلات اليوم بناءً على الجداول الحالية' : 'Recompute late/penalty/status for every row of the selected date using current schedules'}
                >
                  <ArrowCounterClockwise size={18} weight="duotone" />
                  {recomputing
                    ? (language === 'ar' ? 'جاري الحساب...' : 'Recomputing...')
                    : (language === 'ar' ? 'إعادة حساب اليوم' : 'Recompute Day')}
                </Button>
                <Button
                  onClick={openAddDialog}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                  data-testid="admin-add-record-btn"
                >
                  <Plus size={18} weight="bold" />
                  {language === 'ar' ? 'إضافة سجل يدوي' : 'Add Manual Record'}
                </Button>
              </>
            )}
            {isAdmin && (
              <Button
                onClick={exportAttendance}
                disabled={attendance.length === 0}
                className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2 disabled:opacity-50"
                data-testid="export-attendance-btn"
                title={language === 'ar'
                  ? `تصدير سجلات يوم ${selectedDate} كملف Excel/CSV`
                  : `Export attendance for ${selectedDate} as Excel/CSV`}
              >
                <DownloadSimple size={18} weight="bold" />
                {language === 'ar' ? 'تصدير' : 'Export'}
              </Button>
            )}
            <div className="flex items-center gap-1 rounded-md bg-[#E8E6F5] border border-[#C4BFF0] px-1">
              {/* Previous day — in RTL this arrow points right (back), in LTR left */}
              <button
                type="button"
                onClick={() => {
                  // Do the math in UTC to avoid the KSA-timezone off-by-one
                  // bug that used to skip 2 days (local midnight → UTC previous day).
                  const [y, m, d] = selectedDate.split('-').map(Number);
                  const dt = new Date(Date.UTC(y, m - 1, d));
                  dt.setUTCDate(dt.getUTCDate() - 1);
                  setSelectedDate(dt.toISOString().slice(0, 10));
                }}
                title={language === 'ar' ? 'اليوم السابق' : 'Previous day'}
                aria-label={language === 'ar' ? 'اليوم السابق' : 'Previous day'}
                className="p-1.5 rounded text-[#655dc6] hover:bg-[#C4BFF0]/60 transition-colors"
                data-testid="prev-day-btn"
              >
                {language === 'ar' ? <CaretRight size={16} weight="bold" /> : <CaretLeft size={16} weight="bold" />}
              </button>
              <Calendar size={16} className="text-[#655dc6]/70 mx-0.5" />
              <Input
                type="date"
                value={selectedDate}
                max={todayKsa()}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-auto bg-transparent border-0 px-1 focus-visible:ring-0 h-8"
                data-testid="date-picker"
              />
              {/* Next day — disabled when we're already on today */}
              <button
                type="button"
                onClick={() => {
                  if (selectedDate >= todayKsa()) return;
                  const [y, m, d] = selectedDate.split('-').map(Number);
                  const dt = new Date(Date.UTC(y, m - 1, d));
                  dt.setUTCDate(dt.getUTCDate() + 1);
                  const iso = dt.toISOString().slice(0, 10);
                  if (iso <= todayKsa()) setSelectedDate(iso);
                }}
                disabled={selectedDate >= todayKsa()}
                title={language === 'ar' ? 'اليوم التالي' : 'Next day'}
                aria-label={language === 'ar' ? 'اليوم التالي' : 'Next day'}
                className="p-1.5 rounded text-[#655dc6] hover:bg-[#C4BFF0]/60 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="next-day-btn"
              >
                {language === 'ar' ? <CaretLeft size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
              </button>
            </div>
            <div className="flex items-center gap-2 flex-1 min-w-[260px] sm:min-w-[300px]">
              <MagnifyingGlass size={18} className="text-[#655dc6] shrink-0" />
              <Input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={language === 'ar' ? 'ابحث باسم أو رقم الموظف' : 'Search by name or employee #'}
                className="bg-[#E8E6F5] border-[#C4BFF0] flex-1 min-w-0 text-sm placeholder:text-[13px]"
                data-testid="attendance-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="text-[#655dc6]/60 hover:text-[#655dc6] text-xs shrink-0"
                  data-testid="attendance-search-clear"
                  title={language === 'ar' ? 'مسح البحث' : 'Clear search'}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        <Card className="border border-[#C4BFF0]">
          <CardHeader>
            <CardTitle className="text-lg font-medium text-[#655dc6] flex items-center gap-2">
              <Clock size={22} weight="duotone" className="text-[#655dc6]" />
              {selectedDate}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Bulk-waive action bar — appears whenever the admin has
                selected at least one row with an active penalty. */}
            {isAdmin && selectedForWaive.size > 0 && (
              <div className="mb-3 flex items-center justify-between gap-3 bg-orange-50 border border-orange-200 rounded-md px-3 py-2" data-testid="bulk-waive-bar">
                <div className="text-sm text-orange-800 font-medium">
                  {language === 'ar'
                    ? `تم تحديد ${selectedForWaive.size} موظف${selectedForWaive.size >= 3 && selectedForWaive.size <= 10 ? 'ين' : ''}`
                    : `${selectedForWaive.size} employee${selectedForWaive.size > 1 ? 's' : ''} selected`}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs border-[#C4BFF0] text-[#655dc6]"
                    onClick={() => setSelectedForWaive(new Set())}
                    data-testid="bulk-waive-clear-btn"
                  >
                    {language === 'ar' ? 'إلغاء التحديد' : 'Clear'}
                  </Button>
                  <Button
                    size="sm"
                    className="h-8 text-xs gap-1 bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={() => setBulkWaiveDialog({ open: true, reason: '' })}
                    data-testid="bulk-waive-open-btn"
                  >
                    <ShieldSlash size={14} weight="duotone" />
                    {language === 'ar' ? 'إعفاء المحدّدين' : 'Waive Selected'}
                  </Button>
                </div>
              </div>
            )}
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
              </div>
            ) : (
              <>
              {(() => {
                const q = searchQuery.trim().toLowerCase();
                const filtered = q ? attendance.filter(r => {
                  const empNumber = (empNo(r.user_id) || r.employee_number || '').toString().toLowerCase();
                  const nameAr = (r.user_name || '').toLowerCase();
                  const nameEn = (r.user_name_en || '').toLowerCase();
                  return empNumber.includes(q) || nameAr.includes(q) || nameEn.includes(q);
                }) : attendance;
                return (
                  <div className="sm:hidden space-y-3" data-testid="attendance-mobile-list">
                    {filtered.length === 0 ? (
                      <p className="text-center py-8 text-[#655dc6]/70 text-sm">
                        {q
                          ? (language === 'ar' ? `لا توجد نتائج لـ "${searchQuery}"` : `No results for "${searchQuery}"`)
                          : t('no_data')}
                      </p>
                    ) : filtered.map((record, index) => {
                      const shifts = (record.scheduled_shifts && record.scheduled_shifts.length > 0)
                        ? record.scheduled_shifts
                        : (record.scheduled_start ? [{ start: record.scheduled_start, end: '' }] : []);
                      const sessionsArr = Array.isArray(record.sessions) ? record.sessions : [];
                      const isMultiSession = sessionsArr.length >= 2;
                      const mainCi = isMultiSession ? sessionsArr[0]?.clock_in : record.clock_in;
                      const mainCo = isMultiSession ? sessionsArr[0]?.clock_out : record.clock_out;
                      const workHoursRaw = record.work_hours_raw != null ? record.work_hours_raw : record.work_hours;
                      return (
                        <div
                          key={index}
                          data-testid={`attendance-card-${record.user_id}-${record.date}`}
                          className="bg-white dark:bg-slate-900 border border-[#C4BFF0] dark:border-slate-700 rounded-xl p-4 shadow-sm"
                        >
                          <div className="flex items-start justify-between gap-2 pb-3 mb-3 border-b border-[#C4BFF0]/40 dark:border-slate-800/80">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <div className="font-semibold text-[#262263] dark:text-slate-100 text-sm">
                                  <EmployeeName name={record.user_name} nameEn={record.user_name_en} badgeData={getBadge(record.user_id)} badgeLanguage={language} />
                                </div>
                                <SecurityBadge record={record} language={language} />
                                {isMultiSession && (
                                  <span className="text-[9px] font-medium bg-violet-100 text-violet-800 border border-violet-300 px-1 py-0 rounded" data-testid={`multi-session-badge-mobile-${index}`}>
                                    ×{sessionsArr.length}
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-[#655dc6]/70 dark:text-slate-400 font-mono mt-0.5">
                                #{empNo(record.user_id)}
                              </div>
                            </div>
                            <Badge className={`${getStatusBadge(record.status)} shrink-0 text-[11px]`} data-testid={`attendance-status-mobile-${record.user_id}-${record.date}`}>
                              {record.status}
                            </Badge>
                          </div>

                          {/* Clock in/out block */}
                          <div className="grid grid-cols-2 gap-2 mb-3">
                            <div className="rounded-lg bg-emerald-50/60 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/40 p-2">
                              <span className="block text-[10px] text-emerald-700 dark:text-emerald-400 font-semibold mb-0.5">{t('clock_in')}</span>
                              <div className="font-mono text-base font-bold tabular-nums text-[#1F2937] dark:text-slate-100 flex items-center gap-1.5">
                                {formatTime(mainCi)}
                                {record.manual_entry && mainCi && (
                                  <span className="text-[9px] font-sans font-medium bg-emerald-100 text-emerald-800 border border-emerald-300 px-1 rounded">
                                    {language === 'ar' ? 'يدوي' : 'M'}
                                  </span>
                                )}
                              </div>
                              {shifts.length > 0 && (
                                <div className="text-[10px] text-emerald-700/70 font-mono mt-0.5">
                                  {language === 'ar' ? 'جدول' : 'Sched'}: {shifts.map(s => s.start || '-').join(' · ')}
                                </div>
                              )}
                            </div>
                            <div className="rounded-lg bg-rose-50/60 dark:bg-rose-900/20 border border-rose-100 dark:border-rose-900/40 p-2">
                              <span className="block text-[10px] text-rose-700 dark:text-rose-400 font-semibold mb-0.5">{t('clock_out')}</span>
                              <div className="font-mono text-base font-bold tabular-nums text-[#1F2937] dark:text-slate-100 flex items-center gap-1.5">
                                {formatTime(mainCo)}
                                {record.manual_entry && mainCo && (
                                  <span className="text-[9px] font-sans font-medium bg-emerald-100 text-emerald-800 border border-emerald-300 px-1 rounded">
                                    {language === 'ar' ? 'يدوي' : 'M'}
                                  </span>
                                )}
                              </div>
                              {shifts.length > 0 && (
                                <div className="text-[10px] text-rose-700/70 font-mono mt-0.5">
                                  {language === 'ar' ? 'جدول' : 'Sched'}: {shifts.map(s => s.end || '-').join(' · ')}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Extra sessions preview */}
                          {isMultiSession && sessionsArr.slice(1).map((s, sIdx) => (
                            <div key={sIdx} className="text-[11px] text-violet-700 font-mono bg-violet-50 border border-violet-200 px-2 py-1 rounded mb-2 flex items-center justify-between gap-2" data-testid={`session-row-mobile-${index}-${sIdx + 1}`}>
                              <span>#{sIdx + 2}: {formatTime(s.clock_in)} → {formatTime(s.clock_out)}</span>
                              {isAdmin && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="min-h-[44px] min-w-[44px] px-2 text-violet-700"
                                  onClick={() => openEditDialog(record, sIdx + 1)}
                                  data-testid={`admin-edit-session-btn-mobile-${record.id}-${sIdx + 1}`}
                                >
                                  <PencilSimple size={14} />
                                </Button>
                              )}
                            </div>
                          ))}

                          {/* Metadata grid */}
                          <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                            <div>
                              <span className="block text-[#655dc6]/60 font-medium mb-0.5">{t('work_hours')}</span>
                              <span className="font-semibold text-slate-800 dark:text-slate-200">{workHoursRaw != null ? formatHoursHM(workHoursRaw) : '—'}</span>
                              {(record.overtime_hours || 0) > 0 && (
                                <div className="text-[10px] text-amber-700 font-semibold">+{formatHoursHM(record.overtime_hours)} {language === 'ar' ? 'إضافي' : 'OT'}</div>
                              )}
                            </div>
                            <div>
                              <span className="block text-[#655dc6]/60 font-medium mb-0.5">{language === 'ar' ? 'تأخير' : 'Late'}</span>
                              {record.late_minutes && record.late_minutes > 0 ? (
                                <span className="font-semibold text-orange-600">{record.late_minutes} {language === 'ar' ? 'د' : 'min'}</span>
                              ) : <span className="text-[#655dc6]/40">—</span>}
                            </div>
                            <div>
                              <span className="block text-[#655dc6]/60 font-medium mb-0.5">{language === 'ar' ? 'جزاء' : 'Penalty'}</span>
                              {record.late_penalty && record.late_penalty > 0 ? (
                                <span className="font-semibold text-red-600" data-testid={`penalty-mobile-${record.user_id}-${record.date}`}>-{record.late_penalty.toFixed(2)}</span>
                              ) : record.penalty_waived ? (
                                <span className="text-emerald-600 text-[10px] italic">{language === 'ar' ? 'معفى' : 'Waived'}</span>
                              ) : <span className="text-[#655dc6]/40">—</span>}
                            </div>
                            {(record.is_official_holiday || record.is_rest_day) && (
                              <div className="col-span-3">
                                <span className="inline-flex text-[10px] text-purple-700 font-semibold bg-purple-50 border border-purple-200 px-2 py-0.5 rounded" data-testid={`rest-badge-mobile-${record.user_id}-${record.date}`}>
                                  {record.is_official_holiday ? (language === 'ar' ? 'إجازة رسمية' : 'Official holiday') : (language === 'ar' ? 'يوم راحة' : 'Rest day')}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          {isAdmin && (
                            <div className="pt-3 flex flex-wrap items-center gap-2 border-t border-[#C4BFF0]/40 dark:border-slate-800/80">
                              {record.late_penalty > 0 && !record.penalty_waived && (
                                <>
                                  <Checkbox
                                    aria-label={language === 'ar' ? `تحديد ${record.user_name}` : `Select ${record.user_name}`}
                                    checked={selectedForWaive.has(record.user_id)}
                                    onCheckedChange={() => toggleRowSelected(record.user_id)}
                                    data-testid={`bulk-waive-check-mobile-${record.user_id}`}
                                    className="h-5 w-5 shrink-0 self-center"
                                  />
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="min-h-[44px] px-3 border-orange-300 text-orange-700 hover:bg-orange-50 gap-1"
                                    onClick={() => openWaiveDialog(record)}
                                    data-testid={`waive-btn-mobile-${record.user_id}-${record.date}`}
                                  >
                                    <ShieldSlash size={14} weight="duotone" />
                                    <span className="text-xs">{language === 'ar' ? 'إعفاء' : 'Waive'}</span>
                                  </Button>
                                </>
                              )}
                              {record.penalty_waived && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="min-h-[44px] px-3 border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1"
                                  onClick={() => setRestoreDialog({ open: true, record })}
                                  data-testid={`restore-btn-mobile-${record.user_id}-${record.date}`}
                                >
                                  <ArrowCounterClockwise size={14} weight="duotone" />
                                  <span className="text-xs">{language === 'ar' ? 'استرجاع' : 'Restore'}</span>
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="min-h-[44px] px-3 border-[#655dc6]/40 text-[#655dc6] hover:bg-[#E8E6F5] gap-1"
                                onClick={() => openEditDialog(record, isMultiSession ? 0 : null)}
                                data-testid={`admin-edit-btn-mobile-${record.id}`}
                              >
                                <PencilSimple size={14} weight="duotone" />
                                <span className="text-xs">{language === 'ar' ? 'تعديل' : 'Edit'}</span>
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="min-h-[44px] px-3 border-red-300 text-red-700 hover:bg-red-50 gap-1"
                                onClick={() => setDeleteDialog({ open: true, record, reason: '' })}
                                data-testid={`admin-delete-btn-mobile-${record.id}`}
                              >
                                <Trash size={14} weight="duotone" />
                                <span className="text-xs">{language === 'ar' ? 'حذف' : 'Delete'}</span>
                              </Button>
                              {(record.admin_edited || record.admin_created) && (
                                <span
                                  className="inline-flex items-center text-[10px] text-[#655dc6]/80 bg-[#E8E6F5] px-1.5 py-0.5 rounded gap-1 ms-auto"
                                  title={record.admin_edit_reason || record.admin_create_reason || ''}
                                  data-testid={`admin-touched-mobile-${record.id}`}
                                >
                                  <Wrench size={10} weight="duotone" />
                                  {language === 'ar' ? 'معدّل إدارياً' : 'Admin-edited'}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              <div className="hidden sm:block overflow-x-auto">
                <Table data-testid="attendance-table">
                  <TableHeader>
                    <TableRow className="border-[#C4BFF0]">
                      {isAdmin && (
                        <TableHead className="w-9 text-center" data-testid="bulk-waive-head">
                          <Checkbox
                            aria-label={language === 'ar' ? 'تحديد الكل مع جزاء' : 'Select all with penalty'}
                            checked={(() => {
                              const eligible = attendance.filter(r => (r.late_penalty || 0) > 0 && !r.penalty_waived);
                              if (eligible.length === 0) return false;
                              return eligible.every(r => selectedForWaive.has(r.user_id));
                            })()}
                            onCheckedChange={(v) => {
                              const eligibleIds = attendance
                                .filter(r => (r.late_penalty || 0) > 0 && !r.penalty_waived)
                                .map(r => r.user_id);
                              setSelectedForWaive(v ? new Set(eligibleIds) : new Set());
                            }}
                            data-testid="bulk-waive-select-all"
                          />
                        </TableHead>
                      )}
                      <TableHead className="text-start">{t('employee_number')}</TableHead>
                      <TableHead className="text-start">{t('employee_name')}</TableHead>
                      <TableHead className="text-start bg-emerald-50/60 border-l border-r border-emerald-100">{language === 'ar' ? 'بداية الجدول' : 'Sched. Start'}</TableHead>
                      <TableHead className="text-start bg-emerald-50/60">{t('clock_in')}</TableHead>
                      <TableHead className="text-start bg-rose-50/60">{t('clock_out')}</TableHead>
                      <TableHead className="text-start bg-rose-50/60 border-l border-r border-rose-100">{language === 'ar' ? 'نهاية الجدول' : 'Sched. End'}</TableHead>
                      <TableHead className="text-start">{t('work_hours')}</TableHead>
                      <TableHead className="text-start">{language === 'ar' ? 'تأخير' : 'Late'}</TableHead>
                      <TableHead className="text-start">{language === 'ar' ? 'جزاء' : 'Penalty'}</TableHead>
                      <TableHead className="text-start">Status</TableHead>
                      <TableHead className="text-start">{language === 'ar' ? 'إجراء' : 'Action'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      // Client-side search across name(ar), name(en), and
                      // employee_number. Case-insensitive. Returns the
                      // original list when query is empty.
                      const q = searchQuery.trim().toLowerCase();
                      const filtered = q ? attendance.filter(r => {
                        const empNumber = (empNo(r.user_id) || r.employee_number || '').toString().toLowerCase();
                        const nameAr = (r.user_name || '').toLowerCase();
                        const nameEn = (r.user_name_en || '').toLowerCase();
                        return empNumber.includes(q) || nameAr.includes(q) || nameEn.includes(q);
                      }) : attendance;

                      if (filtered.length === 0) {
                        return (
                          <TableRow>
                            <TableCell colSpan={isAdmin ? 12 : 11} className="text-center py-8 text-[#655dc6]/70">
                              {q
                                ? (language === 'ar' ? `لا توجد نتائج لـ "${searchQuery}"` : `No results for "${searchQuery}"`)
                                : t('no_data')}
                            </TableCell>
                          </TableRow>
                        );
                      }

                      return filtered.map((record, index) => {
                        // Schedule cells — show one or two shifts stacked.
                        // Fallback to the legacy `scheduled_start` field when
                        // the new `scheduled_shifts` array isn't present.
                        const shifts = (record.scheduled_shifts && record.scheduled_shifts.length > 0)
                          ? record.scheduled_shifts
                          : (record.scheduled_start ? [{ start: record.scheduled_start, end: '' }] : []);
                        const renderScheduleSide = (which) => {
                          if (shifts.length === 0) return <span className="text-[#655dc6]/30">-</span>;
                          return (
                            <div className="leading-tight font-mono text-xs">
                              {shifts.map((s, i) => (
                                <div key={i} className={i > 0 ? 'mt-0.5 pt-0.5 border-t border-dashed border-[#C4BFF0]/60' : ''}>
                                  {s[which] || '-'}
                                </div>
                              ))}
                            </div>
                          );
                        };

                        // ─── Multi-session expansion ────────────────────────
                        // When the record has multiple punch sessions (e.g.
                        // employee stepped out & came back), render an extra
                        // row per session so each can be edited individually.
                        // The MAIN row shows session-0 in/out + all summary
                        // columns (work hours, late, penalty, status, actions).
                        // Sub-rows show only the session's in/out + an
                        // inline edit button targeting that session.
                        const sessionsArr = Array.isArray(record.sessions) ? record.sessions : [];
                        const isMultiSession = sessionsArr.length >= 2;
                        const mainCi = isMultiSession ? sessionsArr[0]?.clock_in : record.clock_in;
                        const mainCo = isMultiSession ? sessionsArr[0]?.clock_out : record.clock_out;
                        return (
                        <React.Fragment key={index}>
                        <TableRow className="border-[#C4BFF0]">
                          {isAdmin && (
                            <TableCell className="w-9 text-center align-middle">
                              {(record.late_penalty || 0) > 0 && !record.penalty_waived ? (
                                <Checkbox
                                  aria-label={language === 'ar' ? `تحديد ${record.user_name}` : `Select ${record.user_name}`}
                                  checked={selectedForWaive.has(record.user_id)}
                                  onCheckedChange={() => toggleRowSelected(record.user_id)}
                                  data-testid={`bulk-waive-check-${record.user_id}`}
                                />
                              ) : <span className="text-[#655dc6]/20 text-xs">—</span>}
                            </TableCell>
                          )}
                          <TableCell className="text-[#655dc6]/80 font-mono text-xs">{empNo(record.user_id)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              <EmployeeName name={record.user_name} nameEn={record.user_name_en} badgeData={getBadge(record.user_id)} badgeLanguage={language} />
                              <SecurityBadge record={record} language={language} />
                              {isMultiSession && (
                                <span
                                  className="text-[9px] font-medium bg-violet-100 text-violet-800 border border-violet-300 px-1 py-0 rounded"
                                  title={language === 'ar' ? `عدد البصمات: ${sessionsArr.length}` : `Sessions: ${sessionsArr.length}`}
                                  data-testid={`multi-session-badge-${index}`}
                                >
                                  ×{sessionsArr.length}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="bg-emerald-50/40">{renderScheduleSide('start')}</TableCell>
                          <TableCell className="text-[#1F2937] bg-emerald-50/40 font-mono text-base font-bold tabular-nums">
                            <div className="flex items-center gap-1.5 justify-center">
                              {formatTime(mainCi)}
                              {isMultiSession && (
                                <span className="text-[9px] font-sans font-medium bg-violet-100 text-violet-800 border border-violet-300 px-1 py-0 rounded">
                                  #1
                                </span>
                              )}
                              {record.manual_entry && (
                                <span
                                  className="text-[9px] font-sans font-medium bg-emerald-100 text-emerald-800 border border-emerald-300 px-1 py-0 rounded"
                                  title={language === 'ar' ? 'إدخال يدوي' : 'Manual entry'}
                                  data-testid={`manual-badge-in-${index}`}
                                >
                                  {language === 'ar' ? 'يدوي' : 'Manual'}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-[#1F2937] bg-rose-50/40 font-mono text-base font-bold tabular-nums">
                            <div className="flex items-center gap-1.5 justify-center">
                              {formatTime(mainCo)}
                              {record.manual_entry && mainCo && (
                                <span
                                  className="text-[9px] font-sans font-medium bg-emerald-100 text-emerald-800 border border-emerald-300 px-1 py-0 rounded"
                                  title={language === 'ar' ? 'إدخال يدوي' : 'Manual entry'}
                                  data-testid={`manual-badge-out-${index}`}
                                >
                                  {language === 'ar' ? 'يدوي' : 'Manual'}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="bg-rose-50/40">{renderScheduleSide('end')}</TableCell>
                          <TableCell className="text-[#655dc6]/70">
                            {(() => {
                              const raw = record.work_hours_raw != null ? record.work_hours_raw : record.work_hours;
                              const ot = record.overtime_hours || 0;
                              const restBadge = (record.is_official_holiday || record.is_rest_day) ? (
                                <div className="text-[10px] text-purple-700 font-semibold mt-0.5" data-testid={`rest-badge-${record.user_id}-${record.date}`}>
                                  {record.is_official_holiday
                                    ? (language === 'ar' ? 'إجازة رسمية' : 'Official holiday')
                                    : (language === 'ar' ? 'يوم راحة' : 'Rest day')}
                                </div>
                              ) : null;
                              if (raw == null) return <>{restBadge}<span>-</span></>;
                              return (
                                <div className="leading-tight">
                                  <div className="font-medium">{formatHoursHM(raw)}</div>
                                  {ot > 0 && (
                                    <div className="text-[10px] text-amber-700 font-semibold mt-0.5" title={language === 'ar' ? 'ساعات إضافية بعد إكمال الدوام' : 'Overtime beyond contracted hours'}>
                                      +{formatHoursHM(ot)} {language === 'ar' ? 'إضافي' : 'OT'}
                                    </div>
                                  )}
                                  {restBadge}
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            {record.late_minutes && record.late_minutes > 0 ? (
                              <span className="text-orange-600 font-medium text-sm">
                                {record.late_minutes} {language === 'ar' ? 'د' : 'min'}
                              </span>
                            ) : <span className="text-[#655dc6]/30">-</span>}
                          </TableCell>
                          <TableCell>
                            {record.late_penalty && record.late_penalty > 0 ? (
                              <span
                                className="text-red-600 font-semibold text-sm cursor-help"
                                data-testid={`penalty-${record.user_id}-${record.date}`}
                                title={
                                  language === 'ar'
                                    ? `تفاصيل الجزاء:\n• تأخر بعد السماح: ${record.late_after_grace_minutes ?? 0} د\n• انصراف مبكر: ${record.early_departure_minutes ?? 0} د\n• الإجمالي: ${record.shortfall_minutes ?? 0} د`
                                    : `Penalty breakdown:\n• Late after grace: ${record.late_after_grace_minutes ?? 0} min\n• Early departure: ${record.early_departure_minutes ?? 0} min\n• Total: ${record.shortfall_minutes ?? 0} min`
                                }
                              >
                                -{record.late_penalty.toFixed(2)}
                              </span>
                            ) : record.penalty_waived ? (
                              <span className="text-emerald-600 text-xs italic" title={record.penalty_waiver_reason || ''}>
                                {language === 'ar' ? `معفى (${(record.original_late_penalty || 0).toFixed(2)})` : `Waived (${(record.original_late_penalty || 0).toFixed(2)})`}
                              </span>
                            ) : <span className="text-[#655dc6]/30">-</span>}
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusBadge(record.status)}>
                              {record.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 flex-wrap">
                              {isAdmin && record.late_penalty > 0 ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs gap-1 border-orange-300 text-orange-700 hover:bg-orange-50 hover:text-orange-800"
                                  onClick={() => openWaiveDialog(record)}
                                  data-testid={`waive-btn-${record.user_id}-${record.date}`}
                                >
                                  <ShieldSlash size={14} weight="duotone" />
                                  {language === 'ar' ? 'إعفاء' : 'Waive'}
                                </Button>
                              ) : (isAdmin && record.penalty_waived) ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
                                  onClick={() => setRestoreDialog({ open: true, record })}
                                  data-testid={`restore-btn-${record.user_id}-${record.date}`}
                                >
                                  <ArrowCounterClockwise size={14} weight="duotone" />
                                  {language === 'ar' ? 'استرجاع' : 'Restore'}
                                </Button>
                              ) : null}
                              {isAdmin && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs gap-1 border-[#655dc6]/40 text-[#655dc6] hover:bg-[#E8E6F5]"
                                  onClick={() => openEditDialog(record, isMultiSession ? 0 : null)}
                                  data-testid={`admin-edit-btn-${record.id}`}
                                >
                                  <PencilSimple size={14} weight="duotone" />
                                {language === 'ar' ? 'تعديل' : 'Edit'}
                                </Button>
                              )}
                              {isAdmin && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs gap-1 border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                                  onClick={() => setDeleteDialog({ open: true, record, reason: '' })}
                                  data-testid={`admin-delete-btn-${record.id}`}
                                >
                                  <Trash size={14} weight="duotone" />
                                  {language === 'ar' ? 'حذف' : 'Delete'}
                                </Button>
                              )}
                              {(record.admin_edited || record.admin_created) && (
                                <span
                                  className="inline-flex items-center text-[10px] text-[#655dc6]/80 bg-[#E8E6F5] px-1.5 py-0.5 rounded gap-1"
                                  title={record.admin_edit_reason || record.admin_create_reason || ''}
                                  data-testid={`admin-touched-${record.id}`}
                                >
                                  <Wrench size={10} weight="duotone" />
                                  {language === 'ar' ? 'معدّل إدارياً' : 'Admin-edited'}
                                </span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        {/* Sub-rows: one per extra session (only when
                            sessionsArr.length >= 2). Each sub-row shows
                            ONLY that session's clock_in / clock_out + a
                            session-specific edit button. */}
                        {isMultiSession && sessionsArr.slice(1).map((s, sIdx) => {
                          const realIdx = sIdx + 1;
                          return (
                            <TableRow key={`${index}-s${realIdx}`} className="border-[#C4BFF0] bg-violet-50/30" data-testid={`session-row-${index}-${realIdx}`}>
                              {isAdmin && <TableCell />}
                              <TableCell />
                              <TableCell className="text-[10px] text-violet-700 italic">
                                {language === 'ar' ? `↩ بصمة إضافية #${realIdx + 1}` : `↩ Extra session #${realIdx + 1}`}
                              </TableCell>
                              <TableCell />
                              <TableCell className="text-[#1F2937] bg-emerald-50/40 font-mono text-base font-bold tabular-nums">
                                <div className="flex items-center gap-1.5 justify-center">
                                  {formatTime(s.clock_in)}
                                  <span className="text-[9px] font-sans font-medium bg-violet-100 text-violet-800 border border-violet-300 px-1 py-0 rounded">
                                    #{realIdx + 1}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="text-[#1F2937] bg-rose-50/40 font-mono text-base font-bold tabular-nums">
                                <div className="flex items-center gap-1.5 justify-center">
                                  {formatTime(s.clock_out)}
                                </div>
                              </TableCell>
                              <TableCell colSpan={5} />
                              <TableCell>
                                {isAdmin && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs gap-1 border-violet-300 text-violet-700 hover:bg-violet-50"
                                    onClick={() => openEditDialog(record, realIdx)}
                                    data-testid={`admin-edit-session-btn-${record.id}-${realIdx}`}
                                  >
                                    <PencilSimple size={14} weight="duotone" />
                                    {language === 'ar' ? 'تعديل البصمة' : 'Edit session'}
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        </React.Fragment>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </div>
              </>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="corrections" className="mt-4">
            <AttendanceCorrections embed />
          </TabsContent>

          <TabsContent value="absentees" className="mt-4 space-y-4" data-testid="absentees-content">
            <Card className="bg-white border-[#C4BFF0]">
              <CardHeader>
                <CardTitle className="text-[#655dc6] flex items-center gap-2">
                  <UserMinus size={20} weight="duotone" />
                  {language === 'ar' ? 'الغائبون' : 'Absentees'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <Label className="text-xs text-[#655dc6]/80 block mb-1">
                      {language === 'ar' ? 'من تاريخ' : 'From'}
                    </Label>
                    <Input
                      type="date"
                      value={absStart}
                      max={todayKsa()}
                      onChange={(e) => setAbsStart(e.target.value)}
                      className="h-9 w-40"
                      data-testid="absentees-start-date"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#655dc6]/80 block mb-1">
                      {language === 'ar' ? 'إلى تاريخ' : 'To'}
                    </Label>
                    <Input
                      type="date"
                      value={absEnd}
                      max={todayKsa()}
                      onChange={(e) => setAbsEnd(e.target.value)}
                      className="h-9 w-40"
                      data-testid="absentees-end-date"
                    />
                  </div>
                  <Button
                    onClick={fetchAbsentees}
                    disabled={absLoading}
                    className="bg-[#655dc6] hover:bg-[#544cb0] text-white h-9"
                    data-testid="absentees-fetch-btn"
                  >
                    {absLoading
                      ? (language === 'ar' ? 'جاري التحميل...' : 'Loading...')
                      : (language === 'ar' ? 'عرض الغائبين' : 'Show Absentees')}
                  </Button>
                  <div className="ms-auto text-xs text-[#655dc6]/70" data-testid="absentees-count">
                    {language === 'ar'
                      ? `إجمالي: ${absentees.length}`
                      : `Total: ${absentees.length}`}
                  </div>
                </div>

                {absentees.length === 0 ? (
                  <div className="text-center py-10 text-[#655dc6]/50 text-sm" data-testid="absentees-empty">
                    {language === 'ar'
                      ? 'لا توجد سجلات غياب في الفترة المحددة. اضغط "عرض الغائبين" لتحديث القائمة.'
                      : 'No absences in the selected range. Click "Show Absentees" to refresh.'}
                  </div>
                ) : (
                  <div className="rounded-lg border border-[#C4BFF0] overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="!text-center text-[#655dc6] text-xs">
                            {language === 'ar' ? 'الرقم الوظيفي' : 'Emp #'}
                          </TableHead>
                          <TableHead className="text-start text-[#655dc6] text-xs">
                            {language === 'ar' ? 'اسم الموظف' : 'Employee Name'}
                          </TableHead>
                          <TableHead className="!text-center text-[#655dc6] text-xs">
                            {language === 'ar' ? 'القسم' : 'Department'}
                          </TableHead>
                          <TableHead className="!text-center text-[#655dc6] text-xs">
                            {language === 'ar' ? 'التاريخ' : 'Date'}
                          </TableHead>
                          <TableHead className="!text-center text-[#655dc6] text-xs">
                            {language === 'ar' ? 'سبب الغياب' : 'Reason'}
                          </TableHead>
                          <TableHead className="!text-center text-[#655dc6] text-xs">
                            {language === 'ar' ? 'ملاحظات' : 'Notes'}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {absentees.map((a, i) => {
                          const label = leaveLabel(a.leave_type);
                          return (
                            <TableRow key={`${a.user_id}-${a.date}-${i}`} data-testid={`absentee-row-${i}`}>
                              <TableCell className="text-center text-[#655dc6]/80 font-mono text-xs">{empNo(a.user_id)}</TableCell>
                              <TableCell>
                                <EmployeeName name={a.user_name} nameEn={a.user_name_en} badgeData={getBadge(a.user_id)} badgeLanguage={language} />
                              </TableCell>
                              <TableCell className="text-center text-xs text-[#655dc6]/80">
                                {a.department || '-'}
                              </TableCell>
                              <TableCell className="text-center text-xs tabular-nums text-[#655dc6]/80" dir="ltr">
                                {a.date}
                              </TableCell>
                              <TableCell className="text-center">
                                {a.status === 'on_leave' ? (
                                  <Badge className={`${label.cls} text-[11px] font-medium`}>
                                    {language === 'ar' ? label.ar : label.en}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-red-100 text-red-700 text-[11px] font-medium">
                                    {language === 'ar' ? 'غياب بدون عذر' : 'Unexcused'}
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-center text-xs text-[#655dc6]/70 max-w-[260px] truncate" title={a.leave_reason || ''}>
                                {a.leave_reason || '-'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          <TabsContent value="security" className="mt-4">
            <SecurityAudit embed />
          </TabsContent>

          <TabsContent value="adminlog" className="mt-4">
            <AdminAuditLogView language={language} />
          </TabsContent>
        </Tabs>

        {/* Admin: Add Manual Attendance Record */}
        <Dialog open={addDialog.open} onOpenChange={(o) => !o && setAddDialog({ open: false, user_id: '', clock_in: '', clock_out: '', status: 'present', reason: '' })}>
          <DialogContent className="sm:max-w-lg" dir={language === 'ar' ? 'rtl' : 'ltr'} data-testid="admin-add-dialog">
            <DialogHeader>
              <DialogTitle className="text-emerald-700 flex items-center gap-2">
                <Plus size={22} weight="duotone" />
                {language === 'ar' ? 'إضافة سجل حضور يدوي' : 'Add Manual Attendance Record'}
              </DialogTitle>
              <DialogDescription>
                {language === 'ar'
                  ? `سيتم إنشاء سجل لتاريخ ${selectedDate}. هذا الإجراء يُسجَّل في سجل التدقيق.`
                  : `Will create a record for ${selectedDate}. Action is recorded in the audit log.`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div>
                <Label className="text-sm">{language === 'ar' ? 'الموظف' : 'Employee'}</Label>
                <select
                  value={addDialog.user_id}
                  onChange={(e) => setAddDialog({ ...addDialog, user_id: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border border-[#C4BFF0] rounded-md bg-white text-sm"
                  data-testid="admin-add-employee-select"
                >
                  <option value="">{language === 'ar' ? '— اختر الموظف —' : '— Select employee —'}</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.employee_number ? `[${emp.employee_number}] ` : ''}{emp.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3" dir="ltr">
                <div>
                  <Label className="text-sm" dir={language === 'ar' ? 'rtl' : 'ltr'}>{language === 'ar' ? 'وقت الحضور' : 'Clock In'}</Label>
                  <Input
                    type="time" lang="en-GB" dir="ltr"
                    value={addDialog.clock_in}
                    onChange={(e) => setAddDialog({ ...addDialog, clock_in: e.target.value })}
                    className="mt-1"
                    data-testid="admin-add-clockin-input"
                  />
                </div>
                <div>
                  <Label className="text-sm" dir={language === 'ar' ? 'rtl' : 'ltr'}>{language === 'ar' ? 'وقت الانصراف' : 'Clock Out'}</Label>
                  <Input
                    type="time" lang="en-GB" dir="ltr"
                    value={addDialog.clock_out}
                    onChange={(e) => setAddDialog({ ...addDialog, clock_out: e.target.value })}
                    className="mt-1"
                    data-testid="admin-add-clockout-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm">{language === 'ar' ? 'الحالة' : 'Status'}</Label>
                <select
                  value={addDialog.status}
                  onChange={(e) => setAddDialog({ ...addDialog, status: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border border-[#C4BFF0] rounded-md bg-white text-sm"
                  data-testid="admin-add-status-select"
                >
                  <option value="present">{language === 'ar' ? 'حاضر' : 'Present'}</option>
                  <option value="late">{language === 'ar' ? 'متأخر' : 'Late'}</option>
                  <option value="absent">{language === 'ar' ? 'غائب' : 'Absent'}</option>
                  <option value="half_day">{language === 'ar' ? 'نصف يوم' : 'Half day'}</option>
                  <option value="on_leave">{language === 'ar' ? 'في إجازة' : 'On Leave'}</option>
                </select>
              </div>
              <div>
                <Label className="text-sm">{language === 'ar' ? 'سبب الإضافة (إلزامي)' : 'Reason (required)'}</Label>
                <Textarea
                  rows={2}
                  placeholder={language === 'ar' ? 'مثال: الموظف نسي تسجيل الحضور بسبب عطل الجهاز' : 'e.g., employee forgot to punch — device outage'}
                  value={addDialog.reason}
                  onChange={(e) => setAddDialog({ ...addDialog, reason: e.target.value })}
                  className="mt-1"
                  data-testid="admin-add-reason-input"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setAddDialog({ open: false, user_id: '', clock_in: '', clock_out: '', status: 'present', reason: '' })}
                  disabled={savingAdminAction}
                  data-testid="admin-add-cancel-btn"
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                  onClick={submitAddRecord}
                  disabled={savingAdminAction}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="admin-add-confirm-btn"
                >
                  {savingAdminAction ? '...' : (language === 'ar' ? 'إنشاء السجل' : 'Create Record')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Admin: Edit Attendance Record */}
        <Dialog open={editDialog.open} onOpenChange={(o) => !o && setEditDialog({ open: false, record: null, clock_in: '', clock_out: '', status: 'present', reason: '', session_index: null })}>
          <DialogContent className="sm:max-w-lg" dir={language === 'ar' ? 'rtl' : 'ltr'} data-testid="admin-edit-dialog">
            <DialogHeader>
              <DialogTitle className="text-[#655dc6] flex items-center gap-2">
                <PencilSimple size={22} weight="duotone" />
                {language === 'ar' ? 'تعديل سجل الحضور' : 'Edit Attendance Record'}
              </DialogTitle>
              <DialogDescription>
                {editDialog.record && (
                  <>
                    {language === 'ar'
                      ? `الموظف: ${editDialog.record.user_name} — التاريخ: ${editDialog.record.date}`
                      : `Employee: ${editDialog.record.user_name} — Date: ${editDialog.record.date}`}
                    {editDialog.session_index !== null && editDialog.session_index !== undefined && (
                      <span className="block mt-1 text-violet-700 font-medium">
                        {language === 'ar'
                          ? `يتم تعديل البصمة رقم ${editDialog.session_index + 1}`
                          : `Editing session #${editDialog.session_index + 1}`}
                      </span>
                    )}
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3" dir="ltr">
                <div>
                  <Label className="text-sm" dir={language === 'ar' ? 'rtl' : 'ltr'}>{language === 'ar' ? 'وقت الحضور' : 'Clock In'}</Label>
                  <Input
                    type="time" lang="en-GB" dir="ltr"
                    value={editDialog.clock_in}
                    onChange={(e) => setEditDialog({ ...editDialog, clock_in: e.target.value })}
                    className="mt-1"
                    data-testid="admin-edit-clockin-input"
                  />
                </div>
                <div>
                  <Label className="text-sm" dir={language === 'ar' ? 'rtl' : 'ltr'}>{language === 'ar' ? 'وقت الانصراف' : 'Clock Out'}</Label>
                  <Input
                    type="time" lang="en-GB" dir="ltr"
                    value={editDialog.clock_out}
                    onChange={(e) => setEditDialog({ ...editDialog, clock_out: e.target.value })}
                    className="mt-1"
                    data-testid="admin-edit-clockout-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm">{language === 'ar' ? 'الحالة' : 'Status'}</Label>
                <select
                  value={editDialog.status}
                  onChange={(e) => setEditDialog({ ...editDialog, status: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border border-[#C4BFF0] rounded-md bg-white text-sm"
                  data-testid="admin-edit-status-select"
                >
                  <option value="present">{language === 'ar' ? 'حاضر' : 'Present'}</option>
                  <option value="late">{language === 'ar' ? 'متأخر' : 'Late'}</option>
                  <option value="absent">{language === 'ar' ? 'غائب' : 'Absent'}</option>
                  <option value="half_day">{language === 'ar' ? 'نصف يوم' : 'Half day'}</option>
                  <option value="on_leave">{language === 'ar' ? 'في إجازة' : 'On Leave'}</option>
                </select>
              </div>
              <div>
                <Label className="text-sm">{language === 'ar' ? 'سبب التعديل (إلزامي)' : 'Reason (required)'}</Label>
                <Textarea
                  rows={2}
                  placeholder={language === 'ar' ? 'مثال: تصحيح نسيان تسجيل الانصراف' : 'e.g., correcting forgotten clock-out'}
                  value={editDialog.reason}
                  onChange={(e) => setEditDialog({ ...editDialog, reason: e.target.value })}
                  className="mt-1"
                  data-testid="admin-edit-reason-input"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setEditDialog({ open: false, record: null, clock_in: '', clock_out: '', status: 'present', reason: '', session_index: null })}
                  disabled={savingAdminAction}
                  data-testid="admin-edit-cancel-btn"
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                  onClick={submitEditRecord}
                  disabled={savingAdminAction}
                  className="bg-[#655dc6] hover:bg-[#544cb0] text-white"
                  data-testid="admin-edit-confirm-btn"
                >
                  {savingAdminAction ? '...' : (language === 'ar' ? 'حفظ التعديلات' : 'Save Changes')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Admin: Delete Attendance Record */}
        <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, record: null, reason: '' })}>
          <DialogContent className="sm:max-w-md" dir={language === 'ar' ? 'rtl' : 'ltr'} data-testid="admin-delete-dialog">
            <DialogHeader>
              <DialogTitle className="text-red-700 flex items-center gap-2">
                <Trash size={22} weight="duotone" />
                {language === 'ar' ? 'حذف سجل الحضور' : 'Delete Attendance Record'}
              </DialogTitle>
              <DialogDescription>
                {deleteDialog.record && (
                  language === 'ar'
                    ? `سيتم حذف سجل ${deleteDialog.record.user_name} لتاريخ ${deleteDialog.record.date} نهائياً. سيتم الاحتفاظ بنسخة في سجل التدقيق.`
                    : `Permanently delete ${deleteDialog.record.user_name}'s record for ${deleteDialog.record.date}. A copy stays in the audit log.`
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div>
                <Label className="text-sm">{language === 'ar' ? 'سبب الحذف (إلزامي)' : 'Reason (required)'}</Label>
                <Textarea
                  rows={2}
                  placeholder={language === 'ar' ? 'مثال: تسجيل مكرر، خطأ في النظام' : 'e.g., duplicate, system error'}
                  value={deleteDialog.reason}
                  onChange={(e) => setDeleteDialog({ ...deleteDialog, reason: e.target.value })}
                  className="mt-1"
                  data-testid="admin-delete-reason-input"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteDialog({ open: false, record: null, reason: '' })}
                  disabled={savingAdminAction}
                  data-testid="admin-delete-cancel-btn"
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                  onClick={submitDeleteRecord}
                  disabled={savingAdminAction}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="admin-delete-confirm-btn"
                >
                  {savingAdminAction ? '...' : (language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Restore Penalty Confirm Dialog */}
        <Dialog open={restoreDialog.open} onOpenChange={(o) => !o && setRestoreDialog({ open: false, record: null })}>
          <DialogContent className="sm:max-w-md" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <DialogHeader>
              <DialogTitle className="text-emerald-700 flex items-center gap-2">
                <ArrowCounterClockwise size={22} weight="duotone" />
                {language === 'ar' ? 'استرجاع الغرامة' : 'Restore Penalty'}
              </DialogTitle>
              <DialogDescription>
                {restoreDialog.record && (
                  language === 'ar'
                    ? `سيتم استرجاع غرامة ${(restoreDialog.record.original_late_penalty || 0).toFixed(2)} ر.س على ${restoreDialog.record.user_name} لتاريخ ${restoreDialog.record.date}.`
                    : `This will restore a ${(restoreDialog.record.original_late_penalty || 0).toFixed(2)} SAR penalty for ${restoreDialog.record.user_name} on ${restoreDialog.record.date}.`
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setRestoreDialog({ open: false, record: null })}
                data-testid="restore-cancel-btn"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={() => restorePenalty(restoreDialog.record)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="restore-confirm-btn"
              >
                {language === 'ar' ? 'تأكيد الاسترجاع' : 'Confirm Restore'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Waive Penalty Dialog */}
        <Dialog open={waiveDialog.open} onOpenChange={(o) => !o && setWaiveDialog({ open: false, record: null, reason: '' })}>
          <DialogContent className="sm:max-w-md" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <DialogHeader>
              <DialogTitle className="text-orange-700 flex items-center gap-2">
                <ShieldSlash size={22} weight="duotone" />
                {language === 'ar' ? 'إعفاء من غرامة التأخير' : 'Waive Late Penalty'}
              </DialogTitle>
              <DialogDescription>
                {waiveDialog.record && (
                  language === 'ar'
                    ? `سيتم إعفاء ${waiveDialog.record.user_name} من غرامة قدرها ${(waiveDialog.record.late_penalty || 0).toFixed(2)} ر.س لتاريخ ${waiveDialog.record.date}`
                    : `Waiving ${(waiveDialog.record.late_penalty || 0).toFixed(2)} SAR penalty for ${waiveDialog.record.user_name} on ${waiveDialog.record.date}`
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <div>
                <Label className="text-sm">{language === 'ar' ? 'سبب الإعفاء (اختياري)' : 'Reason (optional)'}</Label>
                <Textarea
                  rows={3}
                  className="mt-1"
                  placeholder={language === 'ar' ? 'مثال: ظرف طارئ، حادث مروري، تأخير لم يُحتسب جدولياً...' : 'e.g., emergency, traffic accident, scheduling miscount...'}
                  value={waiveDialog.reason}
                  onChange={(e) => setWaiveDialog({ ...waiveDialog, reason: e.target.value })}
                  data-testid="waive-reason-input"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => setWaiveDialog({ open: false, record: null, reason: '' })} disabled={waiving}>
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                  onClick={submitWaive}
                  disabled={waiving}
                  className="bg-orange-600 hover:bg-orange-700"
                  data-testid="waive-confirm-btn"
                >
                  {waiving ? '...' : (language === 'ar' ? 'تأكيد الإعفاء' : 'Confirm Waive')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Bulk Waive Penalty Dialog */}
        <Dialog open={bulkWaiveDialog.open} onOpenChange={(o) => !o && setBulkWaiveDialog({ open: false, reason: '' })}>
          <DialogContent className="sm:max-w-md" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <DialogHeader>
              <DialogTitle className="text-orange-700 flex items-center gap-2">
                <ShieldSlash size={22} weight="duotone" />
                {language === 'ar' ? 'إعفاء جماعي من غرامة التأخير' : 'Bulk Waive Late Penalty'}
              </DialogTitle>
              <DialogDescription>
                {language === 'ar'
                  ? `سيتم إعفاء ${selectedForWaive.size} موظف من غرامة التأخير لتاريخ ${selectedDate}. الصفوف بلا جزاء أو المُعفاة مسبقاً سيتم تجاهلها.`
                  : `Waiving late penalty for ${selectedForWaive.size} employee${selectedForWaive.size > 1 ? 's' : ''} on ${selectedDate}. Rows without a penalty or already waived will be skipped.`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-1">
              <div>
                <Label className="text-sm">{language === 'ar' ? 'سبب الإعفاء (يُطبَّق على الجميع)' : 'Reason (applied to all)'}</Label>
                <Textarea
                  rows={3}
                  placeholder={language === 'ar' ? 'مثال: عطل نظام البصمة صباحاً' : 'e.g. Biometric system outage this morning'}
                  value={bulkWaiveDialog.reason}
                  onChange={(e) => setBulkWaiveDialog({ ...bulkWaiveDialog, reason: e.target.value })}
                  data-testid="bulk-waive-reason-input"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setBulkWaiveDialog({ open: false, reason: '' })}
                  data-testid="bulk-waive-cancel-btn"
                >
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                  onClick={submitBulkWaive}
                  disabled={bulkWaiving || selectedForWaive.size === 0}
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="bulk-waive-confirm-btn"
                >
                  {bulkWaiving
                    ? '...'
                    : (language === 'ar'
                        ? `تأكيد الإعفاء (${selectedForWaive.size})`
                        : `Confirm Waive (${selectedForWaive.size})`)}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={showImportDialog} onOpenChange={resetImportDialog}>
          <DialogContent className="sm:max-w-lg" dir="rtl">
            <DialogHeader>
              <DialogTitle className="text-[#655dc6] flex items-center gap-2">
                <FileXls size={24} weight="duotone" />
                {t('import_attendance')}
              </DialogTitle>
              <DialogDescription>{t('import_instructions')}</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 pt-2">
              {/* Download Template */}
              <div className="bg-[#F2F0FA] border border-[#C4BFF0] rounded-lg p-4">
                <p className="text-sm text-[#655dc6]/80 mb-3">{t('import_instructions')}</p>
                <Button
                  variant="outline"
                  onClick={downloadTemplate}
                  className="border-[#655dc6] text-[#655dc6] hover:bg-[#E8E6F5] gap-2 w-full"
                  data-testid="download-template-btn"
                >
                  <DownloadSimple size={18} weight="bold" />
                  {t('download_template')}
                </Button>
              </div>

              {/* File Upload */}
              <div
                className="border-2 border-dashed border-[#C4BFF0] rounded-lg p-6 text-center cursor-pointer hover:border-[#655dc6] transition-colors"
                onClick={() => fileInputRef.current?.click()}
                data-testid="file-drop-zone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => {
                    setSelectedFile(e.target.files[0]);
                    setImportResult(null);
                  }}
                  data-testid="file-input"
                />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-2 text-[#655dc6]">
                    <FileXls size={28} weight="duotone" />
                    <span className="font-medium">{selectedFile.name}</span>
                  </div>
                ) : (
                  <div className="text-[#655dc6]/60">
                    <UploadSimple size={32} className="mx-auto mb-2" />
                    <p className="text-sm">{t('click_to_upload')}</p>
                  </div>
                )}
              </div>

              {/* Import Result */}
              {importResult && (
                <div className="bg-[#F2F0FA] border border-[#C4BFF0] rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle size={20} weight="fill" />
                    <span className="text-sm font-medium">{t('imported_count')}: {importResult.imported}</span>
                  </div>
                  {importResult.skipped > 0 && (
                    <div className="flex items-center gap-2 text-yellow-600">
                      <WarningCircle size={20} weight="fill" />
                      <span className="text-sm font-medium">{t('skipped_count')}: {importResult.skipped}</span>
                    </div>
                  )}
                  {importResult.errors?.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto">
                      {importResult.errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-500">{err}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={handleImport}
                  disabled={!selectedFile || importing}
                  className="flex-1 bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2"
                  data-testid="confirm-import-btn"
                >
                  {importing ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <UploadSimple size={18} weight="bold" />
                  )}
                  {importing ? t('importing') : t('import')}
                </Button>
                <Button
                  variant="outline"
                  onClick={resetImportDialog}
                  className="border-[#C4BFF0] text-[#655dc6]"
                  data-testid="cancel-import-btn"
                >
                  {t('cancel')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
